import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiIcon,
  MiIconButton,
  MiInputSelectBox,
  ZoomInput,
} from "@miplatform/components";
import {
  BOARD_MODAL_MODE,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICON_VARIANTS,
  PERMISSIONS,
  PINNED_BOARDS_KEY,
  TYPOGRAPHY_VARIANTS,
  VIEWS,
} from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { appStorage, toKebabCase } from "@miplatform/utils";
import {
  mdiAccount,
  mdiAutorenew,
  mdiChevronLeft,
  mdiChevronRight,
  mdiClose,
  mdiEye,
  mdiHeadset,
  mdiPin,
  mdiPinOutline,
  mdiPlus,
  mdiSwapHorizontal,
  mdiSyncCircle,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, InputAdornment, Typography } from "@mui/material";

const CAROUSEL_INTERVAL_OPTIONS = [
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 15, label: "15" },
  { value: 20, label: "20" },
  { value: 25, label: "25" },
  { value: 30, label: "30" },
];

export const BoardsHeader = ({
  boards,
  calendarView,
  selectedTechnician,
  setIsTechnicianDrawerOpen,
  carouselMode,
  carouselInterval,
  setCarouselInterval,
  toggleCarouselMode,
  isCarouselPaused,
  isTechnicianDrawerOpen,
  isTechnicianSelectorOpen,
  toggleTechnicianSelectorDrawer,
  handleBoardModalOpen,
  selectedBoard = {},
  setSelectedBoard,
  onChangePrevBoard,
  onChangeNextBoard,
  onReturn,
  zoom,
  setZoom,
}) => {
  const { hasPermission } = usePermissions();

  const [pinnedBoardIds, setPinnedBoardIds] = useState(() => {
    try {
      return JSON.parse(appStorage.getItem(PINNED_BOARDS_KEY)) || [];
    } catch {
      return [];
    }
  });

  const pinnedBoards = useMemo(
    () => boards.filter((board) => pinnedBoardIds.includes(board.id)),
    [boards, pinnedBoardIds]
  );

  const isBoardPinned = useMemo(
    () => pinnedBoardIds.includes(selectedBoard?.id),
    [pinnedBoardIds, selectedBoard]
  );
  const { classes, theme } = useStyles({
    isTechnicianDrawerOpen,
    isCarouselPaused,
    carouselMode,
    isBoardPinned,
    selectedBoard,
  });

  useEffect(() => {
    appStorage.setItem(PINNED_BOARDS_KEY, JSON.stringify(pinnedBoardIds));
  }, [pinnedBoardIds]);

  const addPinnedBoard = useCallback(
    (board) => {
      if (pinnedBoards.length >= 3) {
        toastService.warning({
          text: "You can only pin up to 3 boards.",
          subtext: "Please unpin a board before pinning a new one.",
        });
        return;
      }
      setPinnedBoardIds([...pinnedBoards.map((val) => val.id), board.id]);
    },
    [pinnedBoards]
  );

  const removePinnedBoard = useCallback(
    (board) => {
      setPinnedBoardIds(
        pinnedBoards.filter((val) => val.id !== board.id).map((val) => val.id)
      );
    },
    [pinnedBoards]
  );

  const handlePinClick = () => {
    if (isBoardPinned) {
      removePinnedBoard(selectedBoard);
    } else {
      addPinnedBoard(selectedBoard);
    }
  };

  const handleBoardChange = useCallback(
    (e) => {
      if (e.target.value === "createNewBoard") {
        handleBoardModalOpen(BOARD_MODAL_MODE.CREATE);
        return;
      }
      setSelectedBoard(boards.find(({ value }) => value === e.target.value));
    },
    [boards, handleBoardModalOpen, setSelectedBoard]
  );

  if (calendarView === VIEWS.DAYS) {
    return (
      <Box className={classes.daysBoardContainer}>
        <Box className={classes.boardSelectionContainer}>
          <MiIcon path={mdiAccount} size={0.65} className={classes.boardIcon} />
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            variant={TYPOGRAPHY_VARIANTS.BODY2}
          >
            {`Viewing: ${selectedTechnician?.name}'s Schedule`}
          </Typography>
        </Box>
        <Box className={classes.boardActionsContainer}>
          <MiButton
            className={classes.techOverviewButton}
            startIcon={{ path: mdiChevronLeft, iconSize: 0.75 }}
            title="Return to Dispatch Board"
            size="small"
            color={BUTTON_COLORS.BLACK}
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={onReturn}
          />
          <MiButton
            className={classes.techOverviewButton}
            startIcon={{ path: mdiSwapHorizontal, iconSize: 0.75 }}
            sx={{
              backgroundColor: isTechnicianSelectorOpen
                ? theme.palette.primary.light
                : null,
              borderColor: isTechnicianSelectorOpen
                ? theme.palette.primary.main
                : null,
            }}
            title="Switch Technicians"
            size="small"
            color={BUTTON_COLORS.BLACK}
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={toggleTechnicianSelectorDrawer}
          />
          <ZoomInput
            value={zoom}
            handleChange={(e) => setZoom(e.target.value)}
            className={classes.zoomController}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.boardsHeader}>
      <Box className={classes.boardSelectionContainer}>
        <MiInputSelectBox
          value={selectedBoard?.value}
          handleChange={handleBoardChange}
          disableNone
          sx={{ minWidth: 200 }}
          name="Select Board"
          placeholder="Select Board"
          InputProps={
            carouselMode
              ? {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className={classes.inputAdornment}
                    >
                      <MiIcon path={mdiEye} size={0.7} />
                      <Typography>
                        {isCarouselPaused ? "Paused:" : "Viewing:"}
                      </Typography>
                    </InputAdornment>
                  ),
                }
              : null
          }
          options={boards.map((board) => ({
            ...board,
            icon: { path: mdiHeadset, size: 0.6 },
          }))}
          menuItemStyle={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.5, 2),
            fontWeight: theme.typography.fontWeightBold,
            fontSize: theme.fontSize.medium,
          }}
          className={classes.boardSelector}
          customOptionProps={
            hasPermission(PERMISSIONS.CALENDAR_BOARDS.CREATE)
              ? {
                  key: "createNewBoard",
                  value: "createNewBoard",
                  testId: "create-new-board-option",
                  element: (
                    <>
                      <MiIcon
                        path={mdiPlus}
                        variant={ICON_VARIANTS.CONTAINED}
                        size={0.5}
                        color={theme.palette.primary.main}
                        style={{
                          borderRadius: "50%",
                          padding: 0,
                        }}
                      />
                      <Typography
                        variant={TYPOGRAPHY_VARIANTS.BODY1}
                        color={theme.palette.primary.main}
                        fontSize={theme.fontSize.medium}
                        fontWeight={theme.typography.fontWeightBold}
                      >
                        Create New
                      </Typography>
                    </>
                  ),
                }
              : null
          }
        />
        {hasPermission(PERMISSIONS.BOARDS_PINS.READ) && (
          <>
            <MiIconButton
              className={classes.pinButton}
              data-testid="mi-pin-board-button"
              style={{
                backgroundColor: isBoardPinned
                  ? theme.palette.primary.light
                  : theme.palette.background.default,
              }}
              onClick={handlePinClick}
            >
              <MiIcon
                path={isBoardPinned ? mdiPin : mdiPinOutline}
                size={0.6}
                color={theme.palette.black.dark}
              />
            </MiIconButton>
            {pinnedBoards.length
              ? pinnedBoards.map((board) => (
                  <Box
                    key={board.id}
                    className={classes.pinnedBoard}
                    style={{
                      backgroundColor:
                        board.id === selectedBoard.id
                          ? theme.palette.primary.light
                          : theme.palette.white.main,
                    }}
                    onClick={() => setSelectedBoard(board)}
                    data-testid={`pinned-board-${toKebabCase(board.name)}`}
                  >
                    <MiIcon
                      path={mdiHeadset}
                      size={0.6}
                      color={theme.palette.black.dark}
                    />
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                      fontWeight={theme.fontWeight.medium}
                    >
                      {board?.name || "Pinned Board"}
                    </Typography>
                    <MiIcon
                      path={mdiClose}
                      size={0.5}
                      data-testid={`remove-pinned-board-${toKebabCase(
                        board.name
                      )}`}
                      color={theme.palette.black.dark}
                      onClick={(e) => {
                        e.stopPropagation();
                        removePinnedBoard(board);
                      }}
                      style={{ cursor: "pointer", marginLeft: 4 }}
                    />
                  </Box>
                ))
              : null}
          </>
        )}
      </Box>
      <Box className={classes.boardActionsContainer}>
        {hasPermission(PERMISSIONS.BOARDS_CAROUSEL.READ) && (
          <Box className={classes.carouselButtonContainer}>
            <MiButton
              variant={BUTTON_VARIANTS.OUTLINED}
              title={carouselMode ? "Disable Carousel" : "Enable Carousel"}
              size="small"
              color={BUTTON_COLORS.BLACK}
              startIcon={{ path: mdiAutorenew, iconSize: 0.6 }}
              className={classes.carouselButton}
              onClick={toggleCarouselMode}
            />
            {carouselMode && (
              <MiInputSelectBox
                value={carouselInterval || 30}
                name="Carousel Interval"
                handleChange={(e) => setCarouselInterval(e.target.value)}
                options={CAROUSEL_INTERVAL_OPTIONS}
                disableNone
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ margin: 0 }}>
                      <MiIcon
                        path={mdiSyncCircle}
                        size={0.7}
                        color={theme.palette.teal.main}
                      />
                    </InputAdornment>
                  ),
                }}
                menuItemStyle={{ padding: theme.spacing(1, 2) }}
                className={classes.boardSelector}
              />
            )}
          </Box>
        )}
        <MiButton
          variant={
            isTechnicianDrawerOpen
              ? BUTTON_VARIANTS.CONTAINED
              : BUTTON_VARIANTS.OUTLINED
          }
          title={
            isTechnicianDrawerOpen ? "Exit Manage Mode" : "Manage Technicians"
          }
          size="small"
          color={
            isTechnicianDrawerOpen ? BUTTON_COLORS.PRIMARY : BUTTON_COLORS.BLACK
          }
          startIcon={{ path: mdiAccount, iconSize: 0.6 }}
          endIcon={
            isTechnicianDrawerOpen ? { path: mdiClose, iconSize: 0.6 } : null
          }
          className={classes.techOverviewButton}
          onClick={() => setIsTechnicianDrawerOpen((state) => !state)}
        />
        <ZoomInput
          value={zoom}
          handleChange={(e) => setZoom(e.target.value)}
          className={classes.zoomController}
        />
        <MiIconButton
          onClick={onChangePrevBoard}
          className={classes.nextButton}
          dataTestId="prev-board-button"
        >
          <MiIcon
            path={mdiChevronLeft}
            size={0.75}
            color={theme.palette.primary.main}
          />
        </MiIconButton>
        <MiIconButton
          onClick={onChangeNextBoard}
          className={classes.nextButton}
          dataTestId="next-board-button"
        >
          <MiIcon
            path={mdiChevronRight}
            size={0.75}
            color={theme.palette.primary.main}
          />
        </MiIconButton>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(
  (
    theme,
    {
      isTechnicianDrawerOpen,
      carouselMode,
      isCarouselPaused,
      isBoardPinned,
      selectedBoard,
    }
  ) => {
    const carouselColor = isCarouselPaused
      ? theme.palette.orange
      : theme.palette.green;
    return {
      boardsHeader: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        overflow: "auto",
        padding: theme.spacing(0, 2, 1, 2),
        backgroundColor: theme.palette.background.default,
      },
      boardSelectionContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
      },
      daysBoardContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 2, 1, 2),
      },
      pinButton: {
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(0.65),
        border: `1px solid ${
          isBoardPinned ? theme.palette.primary.main : theme.palette.black.main
        }`,
        backgroundColor: isBoardPinned
          ? theme.palette.primary.light
          : theme.palette.background.default,
      },
      pinnedBoard: {
        padding: theme.spacing(0, 1),
        border: `1px solid ${theme.palette.black.dark}`,
        cursor: "pointer",
        color: theme.palette.black.dark,
        borderRadius: theme.spacing(0.5),
        display: "flex",
        width: "fit-content",
        whiteSpace: "nowrap",
        alignItems: "center",
        flexDirection: "row",
        gap: theme.spacing(0.5),
      },
      boardActionsContainer: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        margin: theme.spacing(0, 1),
      },
      carouselButtonContainer: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
      },
      carouselButton: {
        padding: theme.spacing(0, 2),
        backgroundColor: carouselMode
          ? carouselColor.light
          : theme.palette.background.default,
        color: carouselMode ? carouselColor.darkAccent : null,
        fontWeight: theme.fontWeight.semiBold,
        height: 24,
        fontSize: theme.fontSize.medium,
        borderColor: carouselMode
          ? carouselColor.darkAccent
          : theme.palette.black.main,
        borderRadius: theme.spacing(0.5),
      },
      techOverviewButton: {
        padding: theme.spacing(0, 2),
        margin: isTechnicianDrawerOpen ? 1 : 0,
        borderColor: theme.palette.common.black,
        height: 24,
        fontWeight: theme.fontWeight.semiBold,
        fontSize: theme.fontSize.medium,
        borderRadius: theme.spacing(0.5),
      },
      nextButton: {
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.5),
        border: `0.25px solid ${theme.palette.grey.accent}`,
        "&:hover": {
          backgroundColor: theme.palette.grey.accent,
        },
      },
      boardSelector: {
        "& .MuiSelect-select": {
          padding: theme.spacing(0, 2),
          display: "flex",
          alignItems: "center",
          borderRadius: theme.spacing(0.5),
          minWidth: 60,
          gap: theme.spacing(0.5),
          fontWeight: theme.fontWeight.semiBold,
          fontSize: theme.fontSize.medium,
          height: 24,
          color: carouselMode
            ? carouselColor.darkAccent
            : selectedBoard.color || theme.palette.black.dark,
        },
        "& .MuiInputBase-root": {
          backgroundColor: carouselMode ? carouselColor.light : null,
          borderRadius: theme.spacing(0.5),
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: carouselMode
              ? carouselColor.darkAccent
              : theme.palette.black.main,
          },
          "&:hover fieldset": {
            borderColor: carouselMode
              ? carouselColor.darkAccent
              : theme.palette.black.main,
          },
          "&.Mui-focused fieldset": {
            borderColor: carouselMode
              ? carouselColor.darkAccent
              : theme.palette.black.main,
          },
        },
      },
      inputAdornment: {
        margin: 0,
        color: carouselMode ? carouselColor.darkAccent : null,
      },
      zoomController: {
        "& .MuiSelect-select": {
          padding: theme.spacing(0, 2),
          display: "flex",
          alignItems: "center",
          height: 24,
          borderRadius: theme.spacing(0.5),
          gap: theme.spacing(0.5),
          fontWeight: theme.fontWeight.semiBold,
          fontSize: theme.fontSize.medium,
          color: theme.palette.black.dark,
        },
        "& .MuiInputBase-root": {
          borderRadius: theme.spacing(0.5),
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: theme.palette.black.main,
          },
        },
      },
    };
  }
);
